import { useRef, useState, useEffect } from "react";
import { submitAudio } from "@/api/audioSubmission";
import { useToast } from "../toast";
import { useLoader } from "@/context/loaderContext";
import "../CodingTest/online.css";
import { CiMicrophoneOn } from "react-icons/ci";
import { CiMicrophoneOff } from "react-icons/ci";
import { useTimerContext } from "@/context/TimerContext";
import CustomAudioPlayer from "./CustomAudioPlayer";

const AudioRecorder = ({
	question_id,
	candidate_id,
	jobId,
	setButtonToggle,
	markAnsweredQuestion,
}: {
	question_id: string;
	candidate_id: string;
	jobId: string | null;
	setButtonToggle: React.Dispatch<React.SetStateAction<boolean>>;
	markAnsweredQuestion: () => void;
}) => {
	const isRecording = useRef<boolean>(false);
	const [isRunning, setIsRunning] = useState<boolean>(false);
	const timerRef = useRef<any>(null);
	const { setTimeLeft, isTimeUp, setIsTimeUp, setIsPaused } = useTimerContext();

	const { setLoading } = useLoader();
	const toast = useToast();
	const mediaStream = useRef<MediaStream | null>(null);
	const mediaRecorder = useRef<MediaRecorder | null>(null);
	const chunks = useRef<Blob[]>([]);
	const [recordedUrl, setRecordedUrl] = useState<string | null>(null);

	const initialAudioSetup = () => {
		setButtonToggle(false);
		// Reset the state when the question changes
		setRecordedUrl(null);
		// setMinutes(2);
		// setSeconds(0);
		setIsRunning(false);
		setTimeLeft(120);
		setIsTimeUp(false);
		if (mediaRecorder.current && mediaRecorder.current.state === "recording") {
			mediaRecorder.current.stop();
		}
		clearInterval(timerRef.current);
		if (mediaStream.current) {
			mediaStream.current.getTracks().forEach((track: MediaStreamTrack) => {
				track.stop();
			});
		}
	};

	useEffect(() => {
		initialAudioSetup();
	}, [question_id, isTimeUp]);

	useEffect(() => {
		if (isTimeUp) {
			stopRecording();
		}
	}, [isTimeUp]);

	const submitAudioBlob = async (blob: Blob) => {
		setLoading(true);
		try {
			setIsPaused(true);
			await submitAudio({ audioBlob: blob, question_id, candidate_id, jobId });
			markAnsweredQuestion();
			if (!isRecording) {
				setTimeLeft(120);
			}
		} catch (error: any) {
			toast.error({
				type: "background",
				duration: 3000,
				status: "Error",
				title: "Error Submiting recording",
				description: error.response?.data?.error || error.response?.data?.message || "",
				open: true,
			});
		} finally {
			setLoading(false);
			setIsPaused(false);
		}
	};

	// useEffect(() => {
	// 	if (isTimeUp) {
	// 		stopRecording();

	// 		initialAudioSetup();
	// 		setIsTimeUp(false);
	// 	}
	// }, [isTimeUp]);

	const startRecording = async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			mediaStream.current = stream;

			mediaRecorder.current = new MediaRecorder(stream, {
				mimeType: "audio/webm",
			});

			mediaRecorder.current.ondataavailable = (e: BlobEvent) => {
				if (e.data.size > 0) chunks.current.push(e.data);
			};

			mediaRecorder.current.onstop = () => {
				const recordedBlob = new Blob(chunks.current, { type: "audio/wav" });
				setRecordedUrl(URL.createObjectURL(recordedBlob));
				chunks.current = [];
				submitAudioBlob(recordedBlob);
				setButtonToggle(true);
			};

			mediaRecorder.current.start();
			setIsRunning(true);
		} catch (error) {
			toast.error({
				type: "background",
				duration: 3000,
				status: "Error",
				title: "Error starting recording",
				description: "Kindly check your microphone permissions",
				open: true,
			});
		}
	};

	const stopRecording = () => {
		console.log("calling");
		setIsRunning(false);
		clearInterval(timerRef.current);

		mediaRecorder.current?.stop();
		mediaStream.current?.getTracks().forEach((track) => track.stop());
		setIsRunning(false);
	};

	// const startTimer = () => {
	// 	// clearInterval(timerRef.current);
	// 	// let secondsCounter = seconds;
	// 	// let minutesCounter = minutes;
	// 	// timerRef.current = setInterval(() => {
	// 	// 	secondsCounter--;
	// 	// 	if (secondsCounter === -1) {
	// 	// 		secondsCounter = 59;
	// 	// 		minutesCounter--;
	// 	// 	}
	// 	// 	if (minutesCounter === 0 && secondsCounter === 0) {
	// 	// 		stopRecording();
	// 	// 	} else {
	// 	// 		setSeconds(secondsCounter);
	// 	// 		setMinutes(minutesCounter);
	// 	// 	}
	// 	// }, 1000);
	// };

	const handleReRecord = () => {
		setRecordedUrl(null);
		startRecording();
		setButtonToggle(false);
	};

	return (
		<>
			<div className="mt-20 w-full">
				<div className="flex justify-center">
					<div className="relative flex justify-center items-center h-40 w-40 rounded-full ">
						{/* Base Circle */}
						<div className="absolute inset-9 rounded-full border-4 border-transparent" />

						{/* Ping Animation Layers */}
						{isRunning && (
							<>
								<div className="absolute inset-5 rounded-full border-4 border-green-400 animate-ping"></div>
								<div className="absolute inset-2 rounded-full border-4 border-green-300 animate-pulse"></div>
								<div className="absolute inset-3 rounded-full border-4 border-green-200 opacity-50 animate-ping"></div>
								<div className="absolute inset-4 rounded-full border-4 border-green-100 opacity-30 animate-pulse"></div>
							</>
						)}

						{/* Central Button */}
						<div className="absolute h-32 w-32 bg-white rounded-full border-2 border-gray-300 flex items-center justify-center">
							<button className="rounded-full text-[#438437] ">
								{isRunning ? (
									<CiMicrophoneOn size={70} />
								) : (
									<CiMicrophoneOff size={70} />
								)}
							</button>
						</div>
					</div>
				</div>
				{/* <!-- Audio Playback Section --> */}
				<div className="h-[180px]">
					{recordedUrl && (
						<>
							<CustomAudioPlayer url={recordedUrl} />
							{/* <audio controls src={recordedUrl} className=" w-72" /> */}
						</>
					)}
				</div>
				<div className="m space-x-5 mx-auto flex justify-center items-center w-full">
					<button
						className={` px-6 py-2 rounded-lg min-w-[150px]  ${isRunning || recordedUrl
							? "border-[#438437] border-1 cursor-not-allowed text-[#438437]"
							: "bg-[#BDE4C3] hover:bg-[#bde4c3d6] text-[#438437]"
							}`}
						onClick={startRecording}
						disabled={isRunning || recordedUrl == ""}
					>
						Start Recording
					</button>

					<button
						className={`px-6 py-2 rounded-lg   min-w-[150px] ${!isRunning
							? "border-[#438437] border-1 cursor-not-allowed text-[#438437]"
							: "bg-[#BDE4C3] hover:bg-[#bde4c3d6] text-[#438437]"
							}`}
						onClick={stopRecording}
						disabled={!isRunning}
					>
						End Recording
					</button>

					<button
						className={`px-6 py-2 rounded-lg min-w-[180px] ${!recordedUrl
							? "border-[#438437] border-1 cursor-not-allowed text-[#438437]"
							: "bg-[#BDE4C3] hover:bg-[#bde4c3d6] text-[#438437]"
							}`}
						onClick={handleReRecord}
						disabled={!recordedUrl}
					>
						Restart Recording
					</button>
				</div>
			</div>
		</>
	);
};

export default AudioRecorder;
